import React, {useState} from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId, Voting} from 'yougile-model'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {useForceUpdate} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Button} from '../../buttons'
import {MessageFooter} from '../chat-message-base'
import {ChatMessageReaction, SeenBy} from '../types'
import {getTaskOrFromNews} from './get-task-or-from-news'
import {showVotingResults} from './is-voting-passed'
import {VotingItemSelectComp} from './voting-item-select-comp'
import {VotingItemViewComp} from './voting-item-view-comp'

interface VotingCompProps {
  voting: Voting
  hubId: DataObjectId | undefined
  msgTs: number | undefined

  timestamp: number
  editedTimestamp: number | undefined
  seenBy: SeenBy | undefined
  seenByTitle: string | undefined
  reactions?: ChatMessageReaction[]
  currentUserId?: DataObjectId
  onReactionSelect?: (reaction: string) => void
}

export const VotingComp = (props: VotingCompProps) => {
  const {voting, hubId, msgTs, reactions, currentUserId, onReactionSelect} =
    props
  const {timestamp, editedTimestamp, seenBy, seenByTitle} = props
  const forceUpdate = useForceUpdate()

  const [data, setData] = useState<boolean[]>([])

  const showResults = showVotingResults(hubId, voting)
  if (showResults) {
    const votes = getCleanedVotes(voting)
    const votedLen = Object.values(votes).length

    return (
      <>
        <div className="mt-16 text-xs-regular text-primary cursor-default">
          {Lang.msg.voting.stat(votedLen)}
        </div>
        <div className="flex flex-col gap-16 mt-12">
          <div className="flex flex-col gap-12 text-xs-regular text-primary">
            {voting.options.map((option, idx) => (
              <VotingItemViewComp
                key={`${idx}_${option}`}
                votes={votes}
                option={option}
                idx={idx}
              />
            ))}
          </div>

          <MessageFooter
            timestamp={timestamp}
            editedTimestamp={editedTimestamp}
            seenBy={seenBy}
            seenByTitle={seenByTitle}
            reactions={reactions}
            currentUserId={currentUserId}
            onReactionSelect={onReactionSelect}
          />
        </div>
      </>
    )
  }

  const doVoting = () => {
    if (!hubId || !msgTs) {
      return
    }

    const hub = getTaskOrFromNews(hubId)
    if (!hub) {
      return
    }

    const msg = hub.chat.messages.get(msgTs)
    if (!msg?.properties?.voting) {
      return
    }
    const voting = msg.properties.voting as Voting
    voting.votes ??= {}

    const result: number[] = getVotes(data)

    voting.votes[globals.currentUserId] = result
    hub.commit()

    forceUpdate()
  }

  return (
    <div className="flex flex-col gap-16 mt-16">
      <div className="flex flex-col gap-12 text-xs-regular text-primary">
        {voting.options.map((option, idx) => (
          <VotingItemSelectComp
            key={`${idx}_${option}`}
            option={option}
            idx={idx}
            multiSelectOn={voting.multiSelectOn}
            value={!!data[idx]}
            onChange={(idx, val) => {
              const newData = voting.multiSelectOn ? Util.clone(data) : []
              newData[idx] = val
              setData(newData)
            }}
          />
        ))}
      </div>

      <Button
        size="small"
        onMouseUp={doVoting}
        disabled={getVotes(data).length === 0}
      >
        {Lang.msg.voting.doVoting}
      </Button>

      <MessageFooter
        timestamp={timestamp}
        editedTimestamp={editedTimestamp}
        seenBy={seenBy}
        seenByTitle={seenByTitle}
        reactions={reactions}
        currentUserId={currentUserId}
        onReactionSelect={onReactionSelect}
      />
    </div>
  )
}

const getCleanedVotes = (
  voting: Voting
): {[userId: DataObjectId]: number[]} => {
  const votes = Util.clone(voting.votes || {})

  if (SharedBoardsClient.isGuest()) {
    return votes
  }

  const box = Sync.getCurrentSandbox()
  const usersVotes = Object.keys(votes) as DataObjectId[]
  const usersIds = box ? box.getAllUsers(false).map(u => u.id) : []

  for (const userId of usersVotes) {
    if (!usersIds.includes(userId)) {
      delete votes[userId]
    }
  }

  return votes
}

const getVotes = (data: boolean[]): number[] => {
  return Object.keys(data)
    .map(k => Number(k))
    .filter(idx => !!data[idx])
}
